// src/pages/ResourcesAdmin.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Chip, Divider, MenuItem
} from "@mui/material";
import {
  getFirestore, collection, addDoc, getDocs, getDoc,setDoc, doc, updateDoc, deleteDoc,
  serverTimestamp, query, orderBy
} from "firebase/firestore";
import { useAuth } from "../authContext";

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function ResourcesAdmin() {
  const db = getFirestore();
  const { user } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const empty = {
    id: "",
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    tagsText: "",
    status: "draft",
    category: "", // ✅ ADD
  };

  const [form, setForm] = useState(empty);
  const isEditing = !!form.id;

  const load = async () => {
    setLoading(true);
    const q = query(collection(db, "resources"), orderBy("updatedAt", "desc"));
    const snap = await getDocs(q);
    setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const tags = useMemo(() => {
    return (form.tagsText || "")
      .split(",")
      .map(x => x.trim())
      .filter(Boolean);
  }, [form.tagsText]);

  const onNew = () => setForm(empty);

  const onEdit = (r) => {
    setForm({
      id: r.id,
      title: r.title || "",
      slug: r.slug || "",
      excerpt: r.excerpt || "",
      content: r.content || "",
      tagsText: (r.tags || []).join(", "),
      status: r.status || "draft",
      category: r.category || "",
    });
  };

  const onSave = async () => {
    const cleanTitle = form.title.trim();
    const cleanSlug = slugify(form.slug || cleanTitle);

    if (!cleanTitle) return alert("Title is required.");
    if (!cleanSlug) return alert("Slug is required.");

    const payload = {
      title: cleanTitle,
      slug: cleanSlug,
      excerpt: form.excerpt || "",
      content: form.content || "",
      tags,
      status: form.status,
      category: (form.category || "").trim(), // ✅ ADD
      updatedAt: serverTimestamp(),
      authorEmail: user?.email || "",
    };

    if (form.status === "published") {
      payload.publishedAt = serverTimestamp();
    }

    if (!isEditing) {
  // Use slug as document id to prevent duplicate articles
  const ref = doc(db, "resources", cleanSlug);

  // Optional safety: prevent accidental overwrite
  const existing = await getDoc(ref);
  if (existing.exists()) {
    alert("An article with this slug already exists. Open it from the list and Edit instead.");
    return;
  }

  await setDoc(ref, {
    ...payload,
    createdAt: serverTimestamp(),
    publishedAt: form.status === "published" ? serverTimestamp() : null,
  });
} else {
  await updateDoc(doc(db, "resources", form.id), payload);
}

    await load();
    onNew();
  };


const onDelete = async (id, title) => {
  if (!window.confirm(`Delete "${title}" permanently?`)) return;

  try {
    await deleteDoc(doc(db, "resources", id));
    await load();
  } catch (err) {
    console.error("Delete failed:", err);
    alert("Failed to delete article.");
  }
};


  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 2 }}>
        Resources Admin
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="Title"
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Slug"
            value={form.slug}
            onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
            helperText="Leave blank to auto-generate from title"
          />
        </Stack>

        <TextField
          fullWidth
          label="Excerpt"
          sx={{ mt: 2 }}
          value={form.excerpt}
          onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))}
        />

        <TextField
            fullWidth
            label="Category"
            sx={{ mt: 2 }}
            value={form.category}
            onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
            helperText="Example: Audit, GST, Practice Management, Templates"
        />


        <TextField
          fullWidth
          label="Tags (comma separated)"
          sx={{ mt: 2 }}
          value={form.tagsText}
          onChange={(e) => setForm(f => ({ ...f, tagsText: e.target.value }))}
        />

        <TextField
          select
          fullWidth
          label="Status"
          sx={{ mt: 2 }}
          value={form.status}
          onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
        >
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="published">Published</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label="Content (HTML)"
          multiline
          minRows={10}
          sx={{ mt: 2 }}
          value={form.content}
          onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
          helperText="For best rendering, store HTML content. (Later you can add a rich text editor.)"
        />

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onSave}>
            {isEditing ? "Update" : "Create"}
          </Button>
          <Button variant="outlined" onClick={onNew}>
            Clear
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Existing Articles
        </Typography>
        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Typography>Loading…</Typography>
        ) : rows.length === 0 ? (
          <Typography>No resources yet.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {rows.map(r => (
              <Paper key={r.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>{r.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      /resources/{r.slug} • {r.status}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                      {(r.tags || []).slice(0, 6).map(t => <Chip key={t} label={t} size="small" />)}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1}>
  <Button variant="outlined" size="small" onClick={() => onEdit(r)}>
    Edit
  </Button>
  <Button
    variant="outlined"
    color="error"
    size="small"
    onClick={() => onDelete(r.id, r.title)}
  >
    Delete
  </Button>
</Stack>

                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
